import { Injectable } from '@angular/core';
import { Store, StoreConfig } from '@datorama/akita';
import { ProviderUiActionResponse } from '../../../ecash/models/provider-ui-action-response.model';
import { GiftCodeModel } from '../../../purchase/models/giftcode/gift-code.model';
import { GiftCodeType } from '../../../purchase/models/giftcode/gift-code-type.model';
import { GiftCodeEntitlementValueType } from '../../../purchase/models/giftcode/gift-code-entitlement-value-type.model';
import { OrderRequest } from '../../models/orders/order-request.model';
import { PaymentAdjustment } from '../../models/orders/payment-adjustment.model';
import { OrderItem, OrderSnapshot } from '../../models/orders/order-item.model';
import { AdjustmentType } from '../../models/orders/adjustment-type.enum';

/* ================================
 * Constants & Storage Keys
 * ================================ */

export const CHECKOUT_SUMMARY_SNAPSHOT_STORAGE_KEY = 'checkout.summary.snapshot';

/* ================================
 * Initial State
 * ================================ */
export interface PaymentState {
  paymentRequest: OrderRequest | null;
}

function initialState(): PaymentState {
  return {
    paymentRequest: null,
  };
}

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'payment-store' })
export class PaymentStore extends Store<PaymentState> {
  constructor() {
    super(initialState());
  }

  /* ================================
   * Public API
   * ================================ */

  /**
   * This method consolidates the provided items and adjustments
   * (such as discounts, gift codes, taxes, and shipping) into a single
   * order object. It computes the total amount by applying
   * all adjustments to the items, ensuring consistent calculation
   * logic across the application.
   */
  createOrder(
    items: readonly OrderItem[],
    adjustments: PaymentAdjustment[] = [],
    giftCodes: GiftCodeModel[] = []
  ): OrderSnapshot {
    const context = this.buildPaymentContext(items, adjustments, giftCodes);
    this.calculateGiftCodeAdjustments(context);

    const order: OrderSnapshot = {
      items: context.items,
      adjustments: context.adjustments,
      totalAmount: this.calculateTotalAmount(context.items, context.adjustments),
      totalItems: context.items.length,
    };

    const paymentRequest = new OrderRequest({
      ...order,
    });

    this.update((state) => ({
      ...state,
      paymentRequest,
    }));

    this.persistPaymentRequest(paymentRequest);

    return order;
  }

  /**
   * Registers a payment whose totals were already calculated by the server, and
   * returns the request it stored.
   *
   * The Checkout V4 endpoints (`api/checkout`) return the tax, shipping, gift
   * code discounts and basket total on the order itself, so there is nothing to
   * work out here - createOrder's item and gift code arithmetic would only risk
   * disagreeing with the amount the provider is actually charging. The caller
   * passes the server's figures straight through.
   *
   * `items` is still carried because it is display data rather than an input to
   * any sum: the provider components read it (GoAheadPay builds its line items
   * from it) and the post-payment summary pages render it from the persisted
   * snapshot.
   */
  createServerCalculatedPayment(request: {
    paymentResponse: ProviderUiActionResponse;
    items?: readonly OrderItem[];
    adjustments?: readonly PaymentAdjustment[];
    /** The server's total for the order, in minor units. */
    totalAmount: number;
    callbackUrl: string;
    successUrl: string;
    failureUrl: string;
  }): OrderRequest {
    const items = request.items ?? [];

    const paymentRequest = new OrderRequest({
      items,
      adjustments: request.adjustments ?? [],
      totalAmount: request.totalAmount,
      // A count of the basket lines, matching what createOrder recorded.
      totalItems: items.length,
      callbackUrl: request.callbackUrl,
      successUrl: request.successUrl,
      failureUrl: request.failureUrl,
      providerId: request.paymentResponse.ProviderActionInformation.ProviderId,
      orderId: request.paymentResponse.OrderId,
    });

    this.update({ paymentRequest });

    return paymentRequest;
  }

  /**
   * Creates and persists a payment request.
   */
  createPayment(
    order: OrderSnapshot,
    paymentResponse: ProviderUiActionResponse,
    callbackUrl: string,
    successUrl: string,
    failureUrl: string
  ) {
    const providerId = paymentResponse.ProviderActionInformation.ProviderId;
    const orderId = paymentResponse.OrderId;

    const paymentRequest = new OrderRequest({
      ...order,
      callbackUrl,
      successUrl,
      failureUrl,
      providerId,
      orderId,
    });

    this.persistPaymentRequest(paymentRequest);
    this.update({ paymentRequest });
  }

  /**
   * Returns the current payment request. Use this to send to payment providers.
   */
  getPaymentRequest(): OrderRequest | null {
    return this.getValue().paymentRequest;
  }

  /**
   * Observable selector for payment request.
   */
  selectPaymentRequest() {
    return this._select((state) => state.paymentRequest);
  }

  /* ================================
   * Private Helpers
   * ================================ */

  /**
   * Builds the payment context by consolidating order items, adjustments, and gift codes.
   */
  private buildPaymentContext(items: readonly OrderItem[], adjustments: PaymentAdjustment[], giftCodes: GiftCodeModel[]) {
    return {
      items,
      adjustments,
      giftCodes: this.getSortedGiftCodesByEntitlementValueType(giftCodes), // In case the gift codes are not ordered properly we re-order it here to be sure
      runningTotalAmnt: this.calculateTotalAmount(items, adjustments),
      runningShippingAmnt: this.calculateShipping(adjustments),
    };
  }

  /**
   * Calculate gift code with running total
   */
  private calculateGiftCodeAdjustments(ctx: ReturnType<typeof this.buildPaymentContext>) {
    if (ctx.giftCodes.length > 0) {
      let giftCodeAdjustments: PaymentAdjustment[] = [];

      ctx.giftCodes.forEach((giftCode) => {
        const discount = this.getGiftCodeDiscount(ctx, giftCode);
        const amount = discount?.amount || 0;
        giftCodeAdjustments.push({ description: giftCode.DisplayText || giftCode.Code, type: AdjustmentType.Discount, amount });

        // We use running amounts to match the old logic
        if (discount?.giftCodeType === GiftCodeType.Shipping) {
          ctx.runningShippingAmnt -= amount || 0;
          ctx.runningShippingAmnt = Math.max(0, ctx.runningShippingAmnt);
        } else {
          ctx.runningTotalAmnt -= amount || 0;
          ctx.runningTotalAmnt = Math.max(0, ctx.runningTotalAmnt);
        }
      });

      // Update context adjustments and total after applying gift codes, for display purposes.
      ctx.adjustments = [...ctx.adjustments, ...giftCodeAdjustments];
    }
  }

  /**
   * Sort giftcodes by value first
   */
  private getSortedGiftCodesByEntitlementValueType(giftCodes: GiftCodeModel[]) {
    let sortedGiftCodes: GiftCodeModel[] = [];

    if (giftCodes?.length === 0) return sortedGiftCodes;

    giftCodes.forEach((giftCode) => {
      if (giftCode.EntitlementValueType === GiftCodeEntitlementValueType.Value) {
        sortedGiftCodes.unshift(giftCode);
      } else {
        sortedGiftCodes.push(giftCode);
      }
    });

    return sortedGiftCodes;
  }

  /**
   * Calculates the total amount by applying all adjustments to the items. This ensures that the total amount is always accurate and reflects all discounts, taxes, shipping fees, and other adjustments in a consistent manner across the application.
   * The total amount sent is coming from the api. The calculation here is for display purposes and to have a single source of truth for the calculation logic.
   */
  private calculateTotalAmount(items: readonly OrderItem[], adjustments: readonly PaymentAdjustment[] = []): number {
    const itemsTotal = items?.reduce((sum, item) => sum + item.qty * item.unitPrice, 0);

    // Handles all adjustments types, including discounts (negative) and fees, shipping, tax, other (positive)
    const adjustmentsTotal = adjustments?.reduce((sum, adjustment) => {
      return adjustment.type === AdjustmentType.Discount ? sum - adjustment.amount : sum + adjustment.amount;
    }, 0);

    const total = itemsTotal + adjustmentsTotal;

    return Math.max(0, total);
  }

  /**
   * Calculate the shipping total
   */
  private calculateShipping(adjustments: PaymentAdjustment[]): number {
    return adjustments
      .filter((adj) => adj.type === AdjustmentType.Shipping)
      .reduce((sum, adjustment) => sum + adjustment.amount, 0);
  }

  /**
   * Persists the payment and store it as a snapshot in session storage. Only used for displaying summary after payment.
   */
  private persistPaymentRequest(paymentRequest: OrderRequest): void {
    const snapshot = {
      data: paymentRequest,
      expiresAt: Date.now() + 5 * 60 * 1000, // 5 minutes
    };

    sessionStorage.setItem(CHECKOUT_SUMMARY_SNAPSHOT_STORAGE_KEY, JSON.stringify(snapshot));
  }

  /**
   * Get gift code adjustments based on the gift code type and entitlement. This ensures that the discount logic is consistent across the application and can be easily maintained in one place.
   */
  private getGiftCodeDiscount(
    ctx: ReturnType<typeof this.buildPaymentContext>,
    giftCode: GiftCodeModel
  ): (PaymentAdjustment & { giftCodeType: GiftCodeType }) | null {
    const description = giftCode.DisplayText || giftCode.Code;
    const type: AdjustmentType = AdjustmentType.Discount;

    let discount = 0;

    const clamp = (value: number) => Math.max(0, value);

    switch (giftCode.GiftCodeAssignType) {
      case GiftCodeType.Order:
        discount = this.calculateOrderDiscount(ctx.runningTotalAmnt, giftCode);
        break;

      case GiftCodeType.Shipping:
        if (ctx.runningShippingAmnt === 0) return null;

        discount = this.calculateShippingDiscount(ctx.runningShippingAmnt, giftCode);
        break;

      case GiftCodeType.Product:
        discount = this.calculateProductDiscount(ctx.items, giftCode);
        break;

      default:
        return null;
    }

    if (discount <= 0) return null;

    return {
      type,
      giftCodeType: giftCode.GiftCodeAssignType,
      description,
      amount: clamp(discount),
    };
  }

  private calculateOrderDiscount(totalAmount: number, giftCode: GiftCodeModel): number {
    switch (giftCode.EntitlementValueType) {
      case GiftCodeEntitlementValueType.Value:
        return giftCode.EntitlementValue;
      case GiftCodeEntitlementValueType.Percentage:
        return (giftCode.EntitlementValue / 100) * totalAmount;
      case GiftCodeEntitlementValueType.NumberOfEntitlements:
        return totalAmount;
      default:
        return 0;
    }
  }

  private calculateShippingDiscount(totalShipping: number, giftCode: GiftCodeModel): number {
    switch (giftCode.EntitlementValueType) {
      case GiftCodeEntitlementValueType.Value:
        return giftCode.EntitlementValue;
      case GiftCodeEntitlementValueType.Percentage:
        return (giftCode.EntitlementValue / 100) * totalShipping;
      case GiftCodeEntitlementValueType.NumberOfEntitlements:
        return totalShipping;
      default:
        return 0;
    }
  }

  private calculateProductDiscount(items: readonly OrderItem[], giftCode: GiftCodeModel): number {
    let discount = 0;
    let appliedCount = 0;

    items
      .filter((item) => item.id === giftCode.EntitlementItemId || item.id === giftCode.EntitlementItemId2)
      .forEach((item) => {
        if (appliedCount < giftCode.EntitlementValue) {
          appliedCount++;
          discount += item.qty * item.unitPrice;
        }
      });

    return discount;
  }
}
